import { createContext, useContext, useCallback, useEffect, FC, ReactNode, useMemo, useRef } from 'react';
import { isEqual } from 'lodash-es';
import useSessionStorage, { SessionStorageKey } from '@/hooks/useSessionStorage';
import { AdditionalFilterState } from '@/utils/cube/hooks';
import { AdditionalChannelTabs, Channel } from '@/models';
import { useLocalFiltersRegistry } from '@/context/LocalFiltersRegistry/LocalFiltersRegistry';
import { LOCAL_FILTERS_KEY_CHANNEL_FOCUS } from '@/context/LocalFiltersRegistry/localFiltersKeys';

type ChannelKey = Channel | AdditionalChannelTabs;

type ChannelFiltersState = Partial<Record<ChannelKey, AdditionalFilterState>>;

interface FiltersContextType {
  currentChannel: ChannelKey;
  getFiltersForChannel: (channel: ChannelKey) => AdditionalFilterState;
  setFiltersForChannel: (channel: ChannelKey, filters: AdditionalFilterState) => void;
  clearFiltersForChannel: (channel: ChannelKey) => void;
  clearAllFilters: () => void;
  /** Returns filters for current channel - use for data queries */
  additionalFilters: AdditionalFilterState;
  /** @deprecated Use setFiltersForChannel instead */
  setAdditionalFilters: (filters: AdditionalFilterState) => void;
  /** @deprecated Use clearAllFilters instead */
  clearFilters: () => void;
}

const DEFAULT_CHANNEL_FILTERS: ChannelFiltersState = {};

const InteractionChannelFilterContext = createContext<FiltersContextType>({
  currentChannel: Channel.VISITS_1_TO_1,
  getFiltersForChannel: () => ({}),
  setFiltersForChannel: () => {},
  clearFiltersForChannel: () => {},
  clearAllFilters: () => {},
  additionalFilters: {},
  setAdditionalFilters: () => {},
  clearFilters: () => {},
});

interface InteractionChannelFiltersProviderProps {
  children: ReactNode;
  channel: ChannelKey;
}

export const InteractionChannelFiltersProvider: FC<InteractionChannelFiltersProviderProps> = ({
  children,
  channel,
}) => {
  const {
    value: channelFilters,
    setValue: setStoredFilters,
    removeValue: clearAllFilters,
  } = useSessionStorage<ChannelFiltersState>(SessionStorageKey.CHANNEL_FOCUS_FILTERS, DEFAULT_CHANNEL_FILTERS);

  const getFiltersForChannel = useCallback(
    (ch: ChannelKey): AdditionalFilterState => {
      return channelFilters[ch] || {};
    },
    [channelFilters],
  );

  const setFiltersForChannel = useCallback(
    (ch: ChannelKey, filters: AdditionalFilterState) => {
      const currentFilters = channelFilters[ch];
      if (isEqual(currentFilters, filters)) {
        return;
      }
      setStoredFilters({
        ...channelFilters,
        [ch]: filters,
      });
    },
    [channelFilters, setStoredFilters],
  );

  const clearFiltersForChannel = useCallback(
    (ch: ChannelKey) => {
      const newFilters = { ...channelFilters };
      delete newFilters[ch];
      setStoredFilters(newFilters);
    },
    [channelFilters, setStoredFilters],
  );

  const additionalFilters = useMemo(() => channelFilters?.[channel] || {}, [channel, channelFilters]);

  // Backward compatibility - sets filters for current channel
  const setAdditionalFilters = useCallback(
    (filters: AdditionalFilterState) => {
      setFiltersForChannel(channel, filters);
    },
    [channel, setFiltersForChannel],
  );

  const { register, unregister } = useLocalFiltersRegistry();
  const channelFiltersRef = useRef(channelFilters);
  channelFiltersRef.current = channelFilters;
  const channelRef = useRef(channel);
  channelRef.current = channel;

  useEffect(() => {
    register(LOCAL_FILTERS_KEY_CHANNEL_FOCUS, {
      getFilters: () => {
        const currentChannel = channelRef.current;
        const filtersForChannel = channelFiltersRef.current[currentChannel];
        if (!filtersForChannel) return {};
        return { [currentChannel]: filtersForChannel };
      },
      setFilters: (filters: ChannelFiltersState) => {
        setStoredFilters(filters);
      },
    });
    return () => {
      unregister(LOCAL_FILTERS_KEY_CHANNEL_FOCUS);
    };
  }, [channel, channelFilters, register, setStoredFilters, unregister]);

  const contextValue = useMemo(
    () => ({
      currentChannel: channel,
      getFiltersForChannel,
      setFiltersForChannel,
      clearFiltersForChannel,
      clearAllFilters,
      additionalFilters,
      setAdditionalFilters,
      clearFilters: clearAllFilters,
    }),
    [
      channel,
      getFiltersForChannel,
      setFiltersForChannel,
      clearFiltersForChannel,
      clearAllFilters,
      additionalFilters,
      setAdditionalFilters,
    ],
  );

  return (
    <InteractionChannelFilterContext.Provider value={contextValue}>{children}</InteractionChannelFilterContext.Provider>
  );
};

export const useInteractionChannelFilters = () => useContext(InteractionChannelFilterContext);
